import type { SungNote } from './types.js'

/**
 * Merge a take's repeated notes the way the reference's are merged (Pitch Trainer Phase 2, plan M4
 * step 4.3; pitfall 1, the take side). Consecutive segments on the same nearest semitone, less than a
 * beat apart, are one sung note: the singer re-articulated a note the reference holds as one pitch
 * event (Bowie, #2440). Pitch Trainer scores no rhythm, so nothing is lost; Melody Trainer, which
 * does, keeps its take unmerged and forgives absorbed repeats instead.
 *
 * The merged pitch is the mean weighted by how long each part was held. Its stability is the less
 * steady part's, so a wavering half is never averaged into a steady note.
 */
export const mergeSungRepeats = (sung: SungNote[], beatSeconds: number): SungNote[] =>
  sung.reduce<SungNote[]>((notes, note) => {
    const last = notes[notes.length - 1]
    const isRepeat =
      last !== undefined &&
      Math.round(last.midi) === Math.round(note.midi) &&
      note.startTime - last.endTime < beatSeconds
    if (!isRepeat) {
      return [...notes, note]
    }
    const lastHeld = last.endTime - last.startTime
    const noteHeld = note.endTime - note.startTime
    const held = lastHeld + noteHeld
    const midi = held > 0 ? (last.midi * lastHeld + note.midi * noteHeld) / held : last.midi
    return [
      ...notes.slice(0, -1),
      {
        startTime: last.startTime,
        endTime: note.endTime,
        midi,
        cents: Math.round((midi - Math.round(midi)) * 100),
        iqrCents: Math.max(last.iqrCents, note.iqrCents),
      },
    ]
  }, [])
